import { useEffect, useMemo, useState } from "react";
import { KnownNote } from "../components/KnownProblems";
import Likeness from "../components/Likeness";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  api,
  assetUrl,
  on,
  type ProfileTreeBench,
  type ProfileTreeEntry,
  type ProjectScenarios,
  type ScenarioDetail,
  type ScenarioList,
  type ScenarioListRow,
  type ScenarioSource,
  type ScenarioVerdict,
} from "../api";
import { Chip } from "../components/StepParts";
import { ScenarioText } from "../components/ScenarioReadable";
import { Dot, verdictWord } from "../components/Verdict";
import { useTitle } from "../useTitle";
import { Board, Loading, Notice, PageHead, Section, Views } from "../components/Look";

type Bench = { id: number; name: string };

/**
 * What a project tests, and how each case has gone on each of its benches.
 *
 * READ-ONLY AND FROM RUNS. The scenarios live in the product's repository;
 * this page shows what the runs recorded they were, and SAYS WHICH RUN: a
 * bench may run any build and each build carries its own tests (on 2026-09-10
 * run25 on the 7.2.72 build recorded 25 cases, run26 on 7.2.66 an hour later
 * 21), so there is no one list of "the project's scenarios". Each list names
 * its run, bench, build and test-data fingerprint; the newest is shown, the
 * others are one click away, and what changed between them is a line, not a
 * guess.
 *
 * A link from a run (`?run=<id>&open=<case>`) opens that run's list and that
 * case, because the reader of an old run wants the case as it was THERE.
 */
export default function Scenarios() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const pid = Number(id);
  const [params, setParams] = useSearchParams();
  const run = Number(params.get("run")) || undefined;
  const open = params.get("open") ?? "";
  const [data, setData] = useState<ProjectScenarios | null>(null);
  const [error, setError] = useState("");
  const [pick, setPick] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"cases" | "profiles">("cases");
  useTitle(data ? t("scenarios.pageTitle", { project: data.project.name }) : t("scenarios.heading"));

  useEffect(() => {
    let live = true;
    setData(null);
    setError("");
    setPick(null);
    api.projectScenarios(pid, run).then(
      (d) => live && setData(d),
      (e) => live && setError(e instanceof Error ? e.message : String(e)),
    );
    return () => {
      live = false;
    };
  }, [pid, run]);

  // A case named in the link is scrolled to once there is something to scroll.
  useEffect(() => {
    if (data && open) document.getElementById(`case-${open}`)?.scrollIntoView?.({ block: "center" });
    // Only on arrival: re-scrolling whenever `open` changes would yank the
    // page from under somebody clicking down the list.
  }, [data]);

  const back = (
    <Link className="text-sm text-slate-500 underline" to={data ? `/projects/${pid}` : "/projects"}>
      {data ? t("scenarios.back", { project: data.project.name }) : t("projects.backToProjects")}
    </Link>
  );
  // The head draws its own back link; this one is for the two states that
  // render before the page exists.
  if (error) {
    return (
      <section>
        {back}
        <Notice tone="error">{error}</Notice>
      </section>
    );
  }
  if (!data) return <Loading label={t("common.loading")} />;

  const requested = data.lists.findIndex((l) => l.requested);
  const at = pick ?? (requested >= 0 ? requested : 0);
  const list: ScenarioList | undefined = data.lists[at];
  // A case opens its own page (K4b). The panel below the list still answers
  // an old ?open= link, and closes from there; a row click goes to the page.
  const toggle = (sid: string) => {
    if (sid !== open) {
      navigate(`/projects/${pid}/cases/${encodeURIComponent(sid)}`);
      return;
    }
    const p = new URLSearchParams(params);
    p.delete("open");
    setParams(p, { replace: true });
  };

  return (
    <section>
      <PageHead
        back={{ to: data ? `/projects/${pid}` : "/projects",
                label: data ? t("scenarios.back", { project: data.project.name }) : t("projects.backToProjects") }}
        title={t("scenarios.heading")}
        answer={list ? t("scenarios.answerCases", { count: list.scenarios.length }) : undefined}
        actions={data?.benches?.[0] ? (
          // The two other views of the same cases (K4): how much they prove,
          // and what doing them by hand costs - for this product's bench.
          <>
            <Link className="btn-quiet" to={`/quality?bench=${data.benches[0].id}`}>{t("caseCtx.coverageView")}</Link>
            <Link className="btn-quiet" to={`/manual?bench=${data.benches[0].id}`}>{t("caseCtx.manualView")}</Link>
          </>
        ) : undefined}
      />
      {/* TWO VIEWS, NOT ONE PAGE STACKED ON ANOTHER. The tree of customers and
          screens and the list of cases answer different questions, and the tree
          plus its own introduction used to sit ABOVE the list, so 149 words
          stood between the answer and the first case. Picking a case in the
          tree switches to the list with that case open, which is what the click
          was always for. */}
      {(data.tree?.length ?? 0) > 0 && (
        <div className="mt-4">
          <Views
            label={t("scenarios.viewLabel")}
            value={view}
            onChange={setView}
            items={[["cases", t("scenarios.viewCases")], ["profiles", t("scenarios.tree.heading")]]}
          />
        </div>
      )}
      {(data.tree?.length ?? 0) > 0 && view === "profiles" ? (
        <ProfileTree
          tree={data.tree ?? []}
          list={list}
          benches={data.benches}
          open={open}
          onOpen={(sid) => {
            toggle(sid);
            setView("cases");
          }}
        />
      ) : !list ? (
        <p className="max-w-2xl text-base text-slate-500">{t("scenarios.empty")}</p>
      ) : (
        <>
          {data.lists.length > 1 && <Versions lists={data.lists} at={at} onPick={setPick} />}
          <Provenance list={list} />
          {open && !list.scenarios.some((r) => r.id === open) && (
            <p className="mt-3 text-sm text-amber-700 dark:text-amber-400">
              {t("scenarios.openNotHere", { id: open })}
            </p>
          )}
          <CaseList
            list={list}
            benches={data.benches}
            query={query}
            onQuery={setQuery}
            open={open}
            onToggle={toggle}
            pid={pid}
            run={list.sources[0]?.run_id}
          />
        </>
      )}
    </section>
  );
}

type TreeLeaf = ProfileTreeEntry & { bench_id: number; bench_name: string };

const squash = (v: string) => v.toLowerCase().replace(/[^a-z0-9]/g, "");
const readable = (id: string) => (id ? id.charAt(0).toUpperCase() + id.slice(1).replace(/[-_]+/g, " ") : "?");

/** The customer's and the screen's names. The bench sends them since
 *  displayos-hil !8; a bench from before sends ids only, and "bawu" and
 *  "landscape21" are not what anybody calls them. So: the names sent, else the
 *  profile's own label where its words spell the ids ("BAWU Landscape 21 ·
 *  replay"), else the id made readable ("side-display" -> "Side display"). */
function names(l: ProfileTreeEntry): { customer: string; screen: string } {
  const head = (l.label.split(" · ")[0] ?? "").trim().split(/\s+/);
  let customer = l.project_label ?? "";
  let rest: string[] = [];
  const want = customer || l.project || "";
  for (let i = 1; i <= head.length; i++) {
    if (squash(head.slice(0, i).join(" ")) === squash(want)) {
      customer = customer || head.slice(0, i).join(" ");
      rest = head.slice(i);
      break;
    }
  }
  const screen = l.device_label
    || (rest.length > 0 && squash(rest.join(" ")) === squash(l.device ?? "") ? rest.join(" ") : readable(l.device ?? ""));
  return { customer: customer || l.project || "?", screen };
}

/**
 * What a project tests, the way people think of it: General tests first,
 * then each customer, each of its screens, and under a screen how it is
 * tested - a recorded journey, storyboards, simulated data. One leaf per
 * execution profile a bench offers, with what its newest run said and the
 * newest campaign stop for it. The cases under a leaf are the ones that run
 * judged - a prepared run carries exactly the profile's selection - so with
 * every profile run once, every case is in exactly one place, and the page
 * says which cases of the list below are not placed yet or sit in two.
 */
function ProfileTree({
  tree, list, benches, open, onOpen,
}: {
  tree: ProfileTreeBench[];
  list: ScenarioList | undefined;
  benches: Bench[];
  open: string;
  onOpen: (sid: string) => void;
}) {
  const { t } = useTranslation();
  const [shown, setShown] = useState<Set<string>>(() => new Set());
  const leaves = useMemo<TreeLeaf[]>(
    () => tree.flatMap((b) => b.profiles.map((p) => ({ ...p, bench_id: b.bench_id, bench_name: b.bench_name }))),
    [tree],
  );
  if (!leaves.length) return null;
  const many = benches.length > 1;
  const general = leaves.filter((l) => l.kind !== "device");
  // Customers and screens in the order the bench lists its profiles.
  const projects = new Map<string, { label: string; devices: Map<string, { label: string; leaves: TreeLeaf[] }> }>();
  for (const l of leaves) {
    if (l.kind !== "device") continue;
    const pk = l.project || "?";
    if (!projects.has(pk)) projects.set(pk, { label: names(l).customer, devices: new Map() });
    const pr = projects.get(pk)!;
    const dk = l.device || "?";
    if (!pr.devices.has(dk)) pr.devices.set(dk, { label: names(l).screen, leaves: [] });
    pr.devices.get(dk)!.leaves.push(l);
  }
  const key = (l: TreeLeaf) => `${l.bench_id}:${l.id}`;
  const flip = (k: string) =>
    setShown((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const openBelow = (sid: string) => {
    onOpen(sid);
    document.getElementById(`case-${sid}`)?.scrollIntoView?.({ block: "center" });
  };
  const places = new Map<string, number>();
  for (const l of leaves) for (const c of l.run?.cases ?? []) places.set(c.id, (places.get(c.id) ?? 0) + 1);
  const nowhere = list ? list.scenarios.filter((r) => !places.has(r.id)).map((r) => r.id) : [];
  const several = [...places].filter(([, n]) => n > 1).map(([id]) => id);
  const leaf = (l: TreeLeaf) => (
    <Leaf key={key(l)} leaf={l} many={many} shown={shown.has(key(l))} onFlip={() => flip(key(l))}
          open={open} onOpen={openBelow} />
  );

  return (
    <div data-testid="profile-tree">
    <Section title={t("scenarios.tree.heading")}>
      <p className="mb-3 max-w-3xl text-sm text-slate-500">{t("scenarios.tree.intro")}</p>
      <ul className="mt-2 space-y-2">
        {general.length > 0 && <Branch label={t("scenarios.tree.general")}>{general.map(leaf)}</Branch>}
        {[...projects.entries()].map(([pk, pr]) => (
          <Branch key={pk} label={pr.label}>
            {[...pr.devices.entries()].map(([dk, d]) => (
              <Branch key={dk} label={d.label} inner>
                {d.leaves.map(leaf)}
              </Branch>
            ))}
          </Branch>
        ))}
      </ul>
      {nowhere.length > 0 && (
        <Notice tone="warn">
          {t("scenarios.tree.notInAny", { count: nowhere.length, list: nowhere.join(", ") })}
        </Notice>
      )}
      {several.length > 0 && (
        <Notice tone="warn">{t("scenarios.tree.inSeveral", { list: several.join(", ") })}</Notice>
      )}
    </Section>
    </div>
  );
}

function Branch({ label, inner, children }: { label: string; inner?: boolean; children: React.ReactNode }) {
  return (
    <li className={inner ? "ml-3 border-l border-slate-200 pl-3 dark:border-slate-700"
                         : "rounded-lg border border-slate-200 p-3 dark:border-slate-800"}>
      <div className="text-base font-semibold text-slate-800 dark:text-slate-200">{label}</div>
      <ul className="mt-1 space-y-1.5">{children}</ul>
    </li>
  );
}

/** One profile: how the screen is tested, what its newest run said, the
 *  newest campaign stop, and - opened - the cases that run judged. */
function Leaf({
  leaf, many, shown, onFlip, open, onOpen,
}: {
  leaf: TreeLeaf;
  many: boolean;
  shown: boolean;
  onFlip: () => void;
  open: string;
  onOpen: (sid: string) => void;
}) {
  const { t } = useTranslation();
  const run = leaf.run;
  // The product names a profile "<customer> <screen> · <how>"; the "how" is
  // said in words, the mode stands in when a label has no such tail.
  const tail = (leaf.label.split(" · ")[1] ?? "").trim().toLowerCase();
  const word = tail.startsWith("replay") || (!tail && leaf.mode === "replay")
    ? t("scenarios.tree.recordedJourney")
    : tail.startsWith("storyboard")
      ? t("scenarios.tree.storyboards")
      : tail.startsWith("fixture")
        ? t("scenarios.tree.fixture")
        : tail.startsWith("simulated") || (!tail && leaf.mode === "simulated")
          ? t("scenarios.tree.simulated")
          : leaf.label;
  const c = leaf.campaign;
  const standing = !c ? "" : c.status === "green"
    ? t("scenarios.tree.campaignGreen")
    : c.status === "running"
      ? t("scenarios.tree.campaignRunning")
      : c.status === "cancelled"
        ? t("scenarios.tree.campaignCancelled")
        : t("scenarios.tree.campaignNotGreen");
  return (
    <li data-testid={`leaf-${leaf.id}`}>
      <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
        <span className="font-medium">{word}</span>
        <span className="text-slate-500">
          {t("scenarios.tree.cases", { count: run ? run.cases.length : leaf.cases ?? 0 })}
          {many ? ` · ${t("scenarios.tree.onBench", { bench: leaf.bench_name })}` : ""}
        </span>
        {run && run.cases.length > 0 && (
          <button type="button" aria-expanded={shown} onClick={onFlip}
                  className="text-xs text-sky-700 underline dark:text-sky-400">
            {shown ? t("scenarios.tree.hide") : t("scenarios.tree.show")}
          </button>
        )}
      </div>
      <p className="text-xs text-slate-600 dark:text-slate-400">
        {run ? (
          <>
            {t("scenarios.tree.last", {
              run: run.name,
              build: run.build || t("scenarios.buildUnknown"),
              when: on(run.started_at),
              result: t("scenarios.tree.result", {
                passed: run.passed, failed: run.failed, xfailed: run.xfailed, skipped: run.skipped,
              }),
            })}{" "}
            <Link className="underline" to={`/runs/${run.id}`}>{t("scenarios.openRun")}</Link>
          </>
        ) : (
          t("scenarios.tree.never")
        )}
      </p>
      {c && (
        <p className="text-xs text-slate-600 dark:text-slate-400">
          <Link className="underline" to={`/campaigns/${c.id}`}>{t("campaign.badge", { id: c.id })}</Link>
          {": "}
          {standing}
        </p>
      )}
      {run && shown && (
        <ul className="mt-1 space-y-0.5">
          {run.cases.map((k) => (
            <li key={k.id} className="flex items-center gap-2 text-xs">
              <Dot verdict={k.verdict} />
              <button type="button" onClick={() => onOpen(k.id)}
                      className={`font-mono underline ${k.id === open ? "font-semibold" : ""}`}>
                {k.id}
              </button>
              <span className="text-slate-600 dark:text-slate-400">{k.name}</span>
              <span className="text-slate-500">{verdictWord(k.verdict)}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** A list's name on its tab: build, run, bench. */
function useSourceLabel() {
  const { t } = useTranslation();
  return (s: ScenarioSource | undefined) =>
    s ? [s.build || t("scenarios.buildUnknown"), s.run_name, s.bench_name].join(" · ") : "?";
}

/** The lists there are, newest first; the page opens on one and the rest wait. */
function Versions({ lists, at, onPick }: { lists: ScenarioList[]; at: number; onPick: (i: number) => void }) {
  const { t } = useTranslation();
  const label = useSourceLabel();
  return (
    <div className="mt-6">
      <div>
        <Views
          label={t("scenarios.lists", { count: lists.length })}
          value={String(at)}
          onChange={(v) => onPick(Number(v))}
          items={lists.map((l, i) => [
            String(i),
            `${label(l.sources[0])}${l.requested ? ` · ${t("scenarios.fromYourRun")}` : ""}`,
          ])}
        />
      </div>
    </div>
  );
}

/** The honest label: which run, bench, build and test data this list is. */
function Provenance({ list }: { list: ScenarioList }) {
  const { t } = useTranslation();
  const a = list.against;
  return (
    <div className="mt-3 space-y-0.5 text-xs text-slate-600 dark:text-slate-400">
      {list.sources.map((s) => (
        <p key={s.run_id}>
          {t("scenarios.asOf", {
            run: s.run_name,
            bench: s.bench_name,
            when: on(s.started_at),
            build: s.build || t("scenarios.buildUnknown"),
          })}{" "}
          <Link className="underline" to={`/runs/${s.run_id}`}>{t("scenarios.openRun")}</Link>
          <span className="ml-2 font-mono text-slate-400">
            {s.product_digest
              ? t("scenarios.testData", { digest: s.product_digest.slice(0, 12) })
              : t("scenarios.noFingerprint")}
          </span>
        </p>
      ))}
      {a && (
        <p>
          {a.added.length === 0 && a.removed.length === 0
            ? t("scenarios.againstSame", { run: a.run_name })
            : t("scenarios.against", { added: a.added.length, removed: a.removed.length, run: a.run_name })}
          {(a.added.length > 0 || a.removed.length > 0) && (
            <span className="ml-1 font-mono text-slate-400">
              {[...a.added.map((x) => `+${x}`), ...a.removed.map((x) => `−${x}`)].join(" ")}
            </span>
          )}
        </p>
      )}
    </div>
  );
}

function matches(row: ScenarioListRow, q: string): boolean {
  const hay = [row.id, row.name, row.group, row.group_name, ...row.verbs, ...row.requires]
    .join(" ")
    .toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
}

function CaseList({
  list, benches, query, onQuery, open, onToggle, pid, run,
}: {
  list: ScenarioList;
  benches: Bench[];
  query: string;
  onQuery: (q: string) => void;
  open: string;
  onToggle: (sid: string) => void;
  pid: number;
  run?: number;
}) {
  const { t } = useTranslation();
  const shown = useMemo(() => list.scenarios.filter((r) => matches(r, query)), [list, query]);
  // The plan's groups in the plan's order (1.2 before 1.10), cases outside
  // the plan last - they are the ones nobody has written a procedure for.
  const groups = useMemo(() => {
    const byKey = new Map<string, { key: string; name: string; rows: ScenarioListRow[] }>();
    for (const r of shown) {
      const key = r.in_plan ? r.group || r.group_name : "";
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          name: key ? `${r.group} ${r.group_name}`.trim() : t("scenarios.outsidePlan"),
          rows: [],
        });
      }
      byKey.get(key)!.rows.push(r);
    }
    return [...byKey.values()].sort((a, b) =>
      !a.key ? 1 : !b.key ? -1 : a.key.localeCompare(b.key, undefined, { numeric: true }),
    );
  }, [shown, t]);

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder={t("scenarios.filter")}
          aria-label={t("scenarios.filter")}
          className="w-80 rounded border border-slate-300 bg-transparent px-2 py-1 text-sm dark:border-slate-700"
        />
        {query && (
          <span className="text-xs text-slate-500">
            {t("scenarios.shown", { shown: shown.length, count: list.scenarios.length })}
          </span>
        )}
      </div>
      {groups.map((g) => (
        <div key={g.key || "-"} className="mt-6">
          <h2 className="mb-2 text-base font-semibold text-slate-800 dark:text-slate-200">{g.name}</h2>
          <Board as="ul">
            {g.rows.map((r) => (
              <Row key={r.id} row={r} benches={benches} open={r.id === open}
                   onToggle={onToggle} pid={pid} run={run} />
            ))}
          </Board>
        </div>
      ))}
    </div>
  );
}

/** Each bench's last result for the case, as a word beside its dot, and - when
 *  that run did not run it - the last result from a run that did, so a run
 *  that skipped everything does not hide the one before it. Words, because a
 *  dot alone is a status shown by colour alone (style guide, section 4). Each
 *  bench has a slot of its own width, so the ids line up down the list. */
function BenchDots({ row, benches }: { row: ScenarioListRow; benches: Bench[] }) {
  const { t } = useTranslation();
  const many = benches.length > 1;
  return (
    <span className="flex shrink-0 flex-wrap items-center gap-x-3">
      {benches.map((b) => {
        const v = row.benches[String(b.id)];
        const who = many ? `${b.name}: ` : "";
        if (!v?.last) {
          return (
            <span key={b.id} title={t("scenarios.never", { bench: b.name })}
                  className="flex w-44 items-center gap-1.5 text-xs text-slate-500">
              <span className="h-2 w-2 shrink-0 rounded-full border border-slate-300 dark:border-slate-600" aria-hidden="true" />
              <span className="truncate">{who}{t("scenarios.noHistory")}</span>
            </span>
          );
        }
        const ran = v.last.verdict === "skipped" && v.executed ? v.executed : null;
        const title =
          t("scenarios.lastOn", { bench: b.name, verdict: verdictWord(v.last.verdict), run: v.last.run_name }) +
          (ran ? ` · ${t("scenarios.lastRan", { verdict: verdictWord(ran.verdict), run: ran.run_name })}` : "");
        return (
          <span key={b.id} className="flex w-44 items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400" title={title}>
            <Dot verdict={v.last.verdict} label={false} />
            <span className="truncate">
              {who}{verdictWord(v.last.verdict)}
              {ran ? `, ${t("scenarios.ranBefore", { verdict: verdictWord(ran.verdict) })}` : ""}
            </span>
          </span>
        );
      })}
    </span>
  );
}

function Row({
  row, benches, open, onToggle, pid, run,
}: {
  row: ScenarioListRow;
  benches: Bench[];
  open: boolean;
  onToggle: (sid: string) => void;
  pid: number;
  run?: number;
}) {
  const { t } = useTranslation();
  return (
    <li id={`case-${row.id}`}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => onToggle(row.id)}
        className="flex w-full flex-wrap items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 dark:hover:bg-slate-900"
      >
        <BenchDots row={row} benches={benches} />
        <span className="font-mono text-xs">{row.id}</span>
        <span className="text-sm">{row.name}</span>
        {row.draft && <Chip>{t("scenarios.draft")}</Chip>}
        <span className="ml-auto flex flex-wrap items-center gap-2">
          {row.verbs.length > 0 && (
            <span className="font-mono text-[11px] text-slate-500">{row.verbs.join(" ")}</span>
          )}
          {row.requires.length > 0 && (
            <span className="text-[11px] text-slate-400">
              {t("scenarios.requires", { list: row.requires.join(", ") })}
            </span>
          )}
        </span>
      </button>
      {open && <Detail pid={pid} sid={row.id} run={run} benches={benches} />}
    </li>
  );
}

/** The case as this list's run recorded it, and its results bench by bench. */
function Detail({ pid, sid, run, benches }: { pid: number; sid: string; run?: number; benches: Bench[] }) {
  const { t } = useTranslation();
  const label = useSourceLabel();
  const [d, setD] = useState<ScenarioDetail | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    setD(null);
    setError("");
    api.projectScenario(pid, sid, run).then(
      (x) => live && setD(x),
      (e) => live && setError(e instanceof Error ? e.message : String(e)),
    );
    return () => {
      live = false;
    };
  }, [pid, sid, run]);
  if (error) return <p className="px-3 pb-2 text-xs text-red-700 dark:text-red-400" role="alert">{error}</p>;
  if (!d) return <Loading label={t("common.loading")} />;
  // The server puts the version of the run asked for first: the one this list is.
  const [v, ...others] = d.versions;
  const plan = (v?.definition.plan ?? {}) as Record<string, unknown>;
  const text = (k: string) => (typeof plan[k] === "string" && plan[k] ? (plan[k] as string) : "");
  return (
    <div className="space-y-2 px-3 pb-3" data-testid="scenario-detail">
      {(["objective", "preconditions", "expected_result"] as const).map((k) =>
        text(k) ? (
          <div key={k}>
            <Label>{t(`scenarios.plan.${k}`)}</Label>
            <p className="text-sm">{text(k)}</p>
          </div>
        ) : null,
      )}
      {v ? (
        <div>
          <Label>{t("scenarios.asWritten", { source: label(v.sources[0]) })}</Label>
          {v.definition.source ? (
            <ScenarioText
              source={v.definition.source}
              verbs={v.definition.verbs}
              sourceClassName="mt-0.5 overflow-x-auto whitespace-pre-wrap break-words rounded bg-slate-50 p-2 font-mono text-[11px] leading-relaxed dark:bg-slate-800"
            />
          ) : (
            <p className="text-xs text-slate-500">{t("scenarios.noSource")}</p>
          )}
          {others.map((o) => (
            <p key={o.product_digest} className="mt-1 text-xs text-slate-500">
              {o.definition.source === v.definition.source
                ? t("scenarios.alsoSame", { source: label(o.sources[0]) })
                : t("scenarios.alsoDiffers", { source: label(o.sources[0]) })}
            </p>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-500">{t("scenarios.notRecorded")}</p>
      )}
      {d.known && <KnownNote k={d.known} />}
      {/* THE REST OF WHAT IS KNOWN ABOUT THE CASE (step K4). Its group's
          reviewed grade used to be on Quality and its by-hand procedure on
          Manual - two pages, each opening on a bench selector. */}
      {(d.quality || d.manual) && (
        <div className="grid gap-4 md:grid-cols-2" data-testid="case-context">
          {d.quality && (
            <div>
              <Label>{t("caseCtx.quality", { group: `${d.quality.id} ${d.quality.name}` })}</Label>
              <p className="mt-1 text-sm">
                <span className="mr-2 inline-block rounded bg-slate-100 px-1.5 font-semibold dark:bg-slate-800">{d.quality.grade}</span>
                {d.quality.verdict}
              </p>
              {d.quality.stale && <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{t("caseCtx.stale")}</p>}
            </div>
          )}
          {d.manual && (
            <div>
              <Label>{t("caseCtx.manual", { minutes: d.manual.minutes ?? "?", priority: d.manual.priority || "—" })}</Label>
              {d.manual.preconditions && <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{t("caseCtx.pre", { text: d.manual.preconditions })}</p>}
              {(d.manual.steps ?? []).length > 0 && (
                <ol className="mt-1 list-decimal pl-5 text-sm">
                  {d.manual.steps.map((st) => <li key={st.n}>{st.text}</li>)}
                </ol>
              )}
              {d.manual.expected && <p className="mt-1 text-sm">{t("caseCtx.expected", { text: d.manual.expected })}</p>}
            </div>
          )}
        </div>
      )}
      {/* WHAT IT EXPECTS, SEEN. A case about a screen was described in words
          only. The golden itself is written and approved in the test editor,
          not here; this is the copy the newest comparison shipped with its
          run, and it says which run. */}
      {(d.expected ?? []).length > 0 && (
        <div data-testid="expected-screens">
          <Label>{t("scenarios.expected", { run: d.expected![0].run_name })}</Label>
          <div className="mt-1 flex flex-wrap gap-2">
            {d.expected!.map((e) => {
              const url = assetUrl(`/api/v1/runs/${e.run_id}/shots/${encodeURIComponent(e.shot)}`);
              return (
                <a key={e.shot} href={url} target="_blank" rel="noreferrer noopener" className="block">
                  <img src={url} alt={t("scenarios.expectedAlt", { golden: e.golden || e.shot })} loading="lazy"
                       className="max-h-40 rounded border border-slate-200 dark:border-slate-700" />
                  <span className="block font-mono text-[11px] text-slate-500">{e.golden || e.shot}</span>
                </a>
              );
            })}
          </div>
        </div>
      )}
      <div>
        <Label>{t("scenarios.history")}</Label>
        {benches.map((b) => (
          <History key={b.id} bench={b.name} items={d.history[String(b.id)] ?? []} />
        ))}
        {benches.map((b) => (
          <Likeness key={`l${b.id}`} items={d.history[String(b.id)] ?? []} />
        ))}
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className="text-xs text-slate-500">{children}</div>;
}

function History({ bench, items }: { bench: string; items: ScenarioVerdict[] }) {
  const { t } = useTranslation();
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
      <span className="w-40 truncate text-slate-500">{bench}</span>
      {items.length === 0 ? (
        <span className="text-slate-400">{t("scenarios.noHistory")}</span>
      ) : (
        items.map((h) => (
          <Link
            key={h.run_id}
            to={`/runs/${h.run_id}`}
            title={[h.run_name, h.verdict, h.build].filter(Boolean).join(" · ")}
            className="flex items-center gap-1 hover:underline"
          >
            <Dot verdict={h.verdict} />
            <span className="font-mono text-[11px]">{h.run_name}</span>
          </Link>
        ))
      )}
    </div>
  );
}
